import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../app/AppContext.jsx";
import { marketLabel, money, km } from "../lib/format.js";
import { api, qs } from "../lib/api.js";
import { narrowModels } from "../lib/catalogue.js";
import { useFetch, useDebounced } from "../lib/useFetch.js";
import CarCard, { CarCardSkeleton } from "../components/CarCard.jsx";
import Filters from "../components/Filters.jsx";
import Pagination from "../components/Pagination.jsx";

const SORTS = ["newest", "price_asc", "price_desc", "year_desc", "mileage_asc"];

/**
 * The catalogue.
 *
 * Filter state lives in the URL, not in component state, so a filtered view is a link a
 * buyer can send to their partner or an accountant — which for a €200,000 purchase is not
 * a nice-to-have.
 */
export default function Stock() {
  const { t, country, lang } = useApp();
  const [params, setParams] = useSearchParams();

  const query = useMemo(() => Object.fromEntries(params.entries()), [params]);
  const [searchText, setSearchText] = useState(query.q || "");
  useEffect(() => { setSearchText(query.q || ""); }, [query.q]);
  const debouncedSearch = useDebounced(searchText, 320);

  const effective = useMemo(() => ({
    ...query,
    q: debouncedSearch || undefined,
    country,                          // landed prices computed server-side per card
    perPage: query.perPage || 24,
    page: query.page || 1,
    sort: query.sort || "newest",
    steering: query.steering ?? "LHD",
  }), [query, debouncedSearch, country]);

  const key = qs(effective);
  const { data, loading, error, reload } = useFetch(() => api.listings(effective), key);

  // Which models belong to which marque, fetched once. Not what the sidebar renders — the
  // facet counts are — but what lets a change of make take its models with it, in the
  // sidebar and on the chips alike.
  const { data: catalogue } = useFetch(() => api.makes(), "meta:makes");

  const update = useCallback((next) => {
    const clean = {};
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === "" || k === "country" || k === "perPage") continue;
      clean[k] = String(v);
    }
    setParams(clean, { replace: false });
  }, [setParams]);

  const reset = useCallback(() => { setSearchText(""); setParams({}, { replace: false }); }, [setParams]);

  const activeChips = useMemo(() => {
    const chips = [];
    for (const k of ["make", "model", "source", "fuel", "body", "transmission"]) {
      if (!query[k]) continue;
      for (const v of query[k].split(",")) chips.push({ key: k, value: v });
    }
    if (query.priceMin || query.priceMax) chips.push({ key: "price", value: `${money(Number(query.priceMin || 0), "EUR", country)} – ${query.priceMax ? money(Number(query.priceMax), "EUR", country) : "∞"}` });
    if (query.yearMin || query.yearMax) chips.push({ key: "year", value: `${query.yearMin || ""}–${query.yearMax || ""}` });
    if (query.mileageMax) chips.push({ key: "mileageMax", value: `< ${km(Number(query.mileageMax), lang)}` });
    return chips;
  }, [query, country, lang]);

  const removeChip = (chip) => {
    const next = { ...query };
    if (chip.key === "price") { delete next.priceMin; delete next.priceMax; }
    else if (chip.key === "year") { delete next.yearMin; delete next.yearMax; }
    else if (["make", "model", "source", "fuel", "body", "transmission"].includes(chip.key)) {
      const rest = next[chip.key].split(",").filter((v) => v !== chip.value);
      rest.length ? (next[chip.key] = rest.join(",")) : delete next[chip.key];
      // Dropping a make drops the models only it covered — otherwise removing "Porsche"
      // from Porsche + Ferrari leaves a 911 filter behind and empties the grid.
      if (chip.key === "make") {
        const models = narrowModels(next.model ? next.model.split(",") : [], rest, catalogue);
        models.length ? (next.model = models.join(",")) : delete next.model;
      }
    } else delete next[chip.key];
    next.page = 1;
    update(next);
  };

  return (
    <div className="wrap page">
      <div className="page__head">
        <div>
          <p className="label">{t("stock.title")}</p>
          <h1 className="h1" style={{ marginTop: 12 }}>
            {data ? t("common.results", { n: data.total.toLocaleString(lang) }) : t("common.loading")}
          </h1>
          <p className="muted small" style={{ marginTop: 10, maxWidth: 560 }}>{t("stock.sub", { country })}</p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <input
            className="input" style={{ minWidth: 220 }} type="search"
            placeholder={t("stock.search")} value={searchText}
            onChange={(e) => { setSearchText(e.target.value); update({ ...query, q: e.target.value, page: 1 }); }}
            onKeyDown={(e) => { if (e.key === "Enter") update({ ...query, q: searchText, page: 1 }); }}
            aria-label={t("stock.search")}
          />
          <select className="select" style={{ width: "auto" }} value={query.sort || "newest"} onChange={(e) => update({ ...query, sort: e.target.value, page: 1 })} aria-label={t("stock.sort")}>
            {SORTS.map((s) => <option key={s} value={s}>{t(`stock.sort.${s}`)}</option>)}
          </select>
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="chips" style={{ marginBottom: 22 }}>
          {activeChips.map((c) => (
            <span key={`${c.key}:${c.value}`} className="chip">
              {c.key === "source" ? marketLabel(c.value, t) : c.value}
              <button onClick={() => removeChip(c)} aria-label={`${t("common.close")} ${c.key === "source" ? marketLabel(c.value, t) : c.value}`}>×</button>
            </span>
          ))}
          <button className="chip" onClick={reset} style={{ cursor: "pointer" }}>{t("stock.clearAll")}</button>
        </div>
      )}

      <div className="layout-split">
        <aside className="sidebar">
          <Filters query={query} facets={data?.facets} catalogue={catalogue} onChange={update} onReset={reset} />
        </aside>

        <div>
          {error ? (
            <div className="empty">
              <p className="empty__title">{t("common.error")}</p>
              <button className="btn btn--ghost btn--sm" onClick={reload}>{t("common.retry")}</button>
            </div>
          ) : loading && !data ? (
            <div className="grid grid--cars">
              {Array.from({ length: 9 }, (_, i) => <CarCardSkeleton key={i} />)}
            </div>
          ) : data?.results?.length ? (
            <>
              <div className="grid grid--cars" style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s" }}>
                {data.results.map((l, i) => <CarCard key={l.id} listing={l} quote={l.quote} priority={i < 6} />)}
              </div>
              <Pagination page={Number(effective.page)} pages={data.pages} onChange={(p) => update({ ...query, page: p })} />
            </>
          ) : (
            <div className="empty">
              <p className="empty__title">{t("common.noResults")}</p>
              <p>{t("common.noResultsBody")}</p>
              <button className="btn btn--ghost btn--sm" style={{ marginTop: 20 }} onClick={reset}>{t("stock.clearAll")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
